// Copyright (c) 2026 Isolated Networks. All rights reserved.
// Proprietary and confidential. Unauthorized copying, distribution, or use
// of this file, via any medium, is strictly prohibited.
// SPDX-License-Identifier: LicenseRef-Proprietary

import React, { useState, useEffect, useRef } from 'react';
import axios from '../utils/axiosConfig';
import { fmtTime } from '../utils/timezoneUtils';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import ContentSkeleton from './ContentSkeleton';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Divider from '@mui/material/Divider';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import LinearProgress from '@mui/material/LinearProgress';
import RefreshIcon from '@mui/icons-material/Refresh';
import LanIcon from '@mui/icons-material/Lan';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import ComputerIcon from '@mui/icons-material/Computer';
import DevicesOtherIcon from '@mui/icons-material/DevicesOther';
import { useTheme } from '../context/ThemeContext';

const StargateVlanClients = ({ ip, apiKey, healthStatus }) => {
  const { isDarkMode } = useTheme();
  const [clientsData, setClientsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const intervalRef = useRef(null);
  // Interval callbacks close over stale state; track "data on screen" via ref.
  const hasDataRef = useRef(false);

  const fetchClients = async (showLoading = true) => {
    if (!ip || !apiKey) {
      setError('IP address or API key is missing');
      return;
    }

    if (showLoading) setLoading(true);

    try {
      const response = await axios.get('/proxy/network/vlan-clients', {
        params: {
          ip: ip,
          api_key: apiKey,
        },
        timeout: 15000,
      });

      if (response.data) {
        // Skip no-op updates so the 30s poll doesn't re-render an unchanged table.
        setClientsData((prev) =>
          JSON.stringify(prev) === JSON.stringify(response.data) ? prev : response.data
        );
        hasDataRef.current = true;
        setLastRefresh(new Date());
        setError(null);
      }
    } catch (err) {
      if (err.response?.status === 404 || err.response?.status === 502) {
        setClientsData(null);
        hasDataRef.current = false;
      } else {
        console.error('[VLAN Clients] Fetch error:', err);
        // Background poll failure with data already on screen: keep showing the
        // stale table instead of injecting an error Alert that shifts the layout.
        if (showLoading || !hasDataRef.current) {
          setError(err.response?.data?.detail || err.message || 'Failed to fetch VLAN clients');
        }
      }
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  useEffect(() => {
    if (healthStatus && healthStatus !== 'green') {
      setClientsData(null);
      hasDataRef.current = false;
      setError(null);
      setLoading(false);
      return;
    }

    fetchClients(true);

    intervalRef.current = setInterval(() => {
      fetchClients(false);
    }, 30000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [ip, apiKey, healthStatus]);

  // Sticky icon per client: keep the last specific classification when a later
  // poll comes back inconclusive, and never show a question mark.
  const iconClassByDeviceRef = useRef({});

  const getDeviceIcon = (client) => {
    const type = (client?.device_type || '').toLowerCase();
    let cls = 'generic';
    if (type.includes('wg') || type.includes('tunnel') || type.includes('wireguard')) {
      cls = 'vpn';
    } else if (
      type.includes('computer') ||
      type.includes('pc') ||
      type.includes('laptop') ||
      type.includes('windows') ||
      type.includes('mac') ||
      type.includes('linux') ||
      type.includes('workstation') ||
      type.includes('server')
    ) {
      cls = 'computer';
    }

    const key = client?.mac_address || client?.ip_address || client?.hostname || '';
    if (cls === 'generic' && key && iconClassByDeviceRef.current[key]) {
      cls = iconClassByDeviceRef.current[key];
    } else if (cls !== 'generic' && key) {
      iconClassByDeviceRef.current[key] = cls;
    }

    if (cls === 'vpn') return <VpnKeyIcon />;
    if (cls === 'computer') return <ComputerIcon />;
    return <DevicesOtherIcon />;
  };

  const onlineCount = clientsData?.clients
    ? clientsData.clients.filter((c) => c.online === true).length
    : 0;
  const poolUsed = clientsData
    ? (clientsData.pool_total || 0) - (clientsData.pool_available || 0)
    : 0;
  const poolPercent =
    clientsData && clientsData.pool_total > 0
      ? Math.round((poolUsed / clientsData.pool_total) * 100)
      : 0;

  if (!ip || !apiKey) {
    return (
      <Card sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
        <CardContent>
          <Alert severity="warning">IP address or API key is missing</Alert>
        </CardContent>
      </Card>
    );
  }

  // Stay hidden until we have data (or a real error). Rendering a skeleton
  // that then vanishes for VLAN-less Stargates would shift the page on load.
  if (!clientsData && !error) {
    return null;
  }

  // The device API has no explicit "VLAN enabled" flag — Stargates without the
  // VLAN function still answer with an empty payload (no clients, untouched
  // pool). Hide the whole card in that state instead of showing zeros.
  const vlanInactive =
    clientsData &&
    (clientsData.total || 0) === 0 &&
    (!Array.isArray(clientsData.clients) || clientsData.clients.length === 0) &&
    poolUsed <= 0;
  if (vlanInactive && !error) {
    return null;
  }

  return (
    <Card sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
      <CardHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LanIcon />
            <Typography variant="h6">VLAN Tunnel Clients</Typography>
            {clientsData && (
              <Chip
                label={`${onlineCount} Online / ${clientsData.total || 0} Total`}
                size="small"
                color={onlineCount > 0 ? 'success' : 'default'}
              />
            )}
          </Box>
        }
        action={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {lastRefresh && (
              <Typography variant="caption" sx={{ color: 'text.secondary', mr: 1 }}>
                {fmtTime(lastRefresh)}
              </Typography>
            )}
            <Tooltip title="Refresh">
              <IconButton onClick={() => fetchClients(true)} size="small" disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        }
      />
      <Divider />
      <CardContent>
        {loading && !clientsData && <ContentSkeleton variant="table" rows={4} columns={4} />}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {clientsData && (
          <Box>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={3}>
                <Box
                  sx={{
                    p: 2,
                    background: isDarkMode ? 'rgba(138, 148, 168, 0.06)' : '#f5f7fa',
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Online Clients
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 'bold',
                      color: onlineCount > 0 ? '#4caf50' : '#f44336',
                    }}
                  >
                    {onlineCount}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Box
                  sx={{
                    p: 2,
                    background: isDarkMode ? 'rgba(138, 148, 168, 0.06)' : '#f5f7fa',
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Total Discovered
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                    {clientsData.total || 0}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Box
                  sx={{
                    p: 2,
                    background: isDarkMode ? 'rgba(138, 148, 168, 0.06)' : '#f5f7fa',
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Pool Capacity
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                    {clientsData.pool_total || 0}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Box
                  sx={{
                    p: 2,
                    background: isDarkMode ? 'rgba(138, 148, 168, 0.06)' : '#f5f7fa',
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Pool Available
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 'bold',
                      color: clientsData.pool_available === 0 ? '#f44336' : 'inherit',
                    }}
                  >
                    {clientsData.pool_available ?? 'N/A'}
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            {clientsData.pool_total > 0 && (
              <Box sx={{ mb: 3 }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    mb: 0.5,
                  }}
                >
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Tunnel Pool Usage
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {poolUsed} / {clientsData.pool_total} ({poolPercent}%)
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={poolPercent}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 4,
                      backgroundColor:
                        poolPercent >= 90 ? '#f44336' : poolPercent >= 70 ? '#ff9800' : '#4caf50',
                    },
                  }}
                />
              </Box>
            )}

            {clientsData.clients && clientsData.clients.length > 0 ? (
              <Box>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                  VLAN Tunnel Devices
                </Typography>
                <TableContainer
                  component={Paper}
                  sx={{
                    background: isDarkMode ? 'rgba(138, 148, 168, 0.04)' : '#ffffff',
                  }}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Hostname</TableCell>
                        <TableCell>LAN IP</TableCell>
                        <TableCell>MAC Address</TableCell>
                        <TableCell>VLAN</TableCell>
                        <TableCell>Bridge</TableCell>
                        <TableCell>VPN IP</TableCell>
                        <TableCell>Namespace</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {clientsData.clients.map((client, idx) => (
                        <TableRow key={idx} hover sx={{ opacity: client.online ? 1 : 0.6 }}>
                          <TableCell>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                              }}
                            >
                              {getDeviceIcon(client)}
                              <Typography variant="body2">
                                {client.hostname || 'Unknown'}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                              {client.ip || 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{
                                fontFamily: 'monospace',
                                fontSize: '0.75rem',
                              }}
                            >
                              {client.mac || 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={`VLAN ${client.vlan_id}`}
                              size="small"
                              sx={{
                                backgroundColor: 'rgba(156, 39, 176, 0.2)',
                                color: '#ce93d8',
                                border: '1px solid rgba(156, 39, 176, 0.4)',
                                fontWeight: 600,
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{
                                fontFamily: 'monospace',
                                fontSize: '0.75rem',
                              }}
                            >
                              {client.bridge || 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{ fontFamily: 'monospace', color: '#81c784' }}
                            >
                              {client.vpn_ip || 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{
                                fontFamily: 'monospace',
                                fontSize: '0.75rem',
                              }}
                            >
                              {client.wg_namespace || 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={client.online ? 'Online' : 'Offline'}
                              size="small"
                              color={client.online ? 'success' : 'error'}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            ) : (
              <Alert severity="info">No active VLAN tunnel clients</Alert>
            )}
          </Box>
        )}

        {!loading && !clientsData && !error && (
          <Alert severity="info">No VLAN client data available</Alert>
        )}
      </CardContent>
    </Card>
  );
};

export default StargateVlanClients;
